/**
 * Por qué la lista de candidatos para la entrevista del solicitante puede
 * verse vacía (SRH-308 / A-29).
 *
 * Antes las tres razones se veían igual. La pantalla de agendar hacía
 * `if (respuesta.ok)` sin `else`, así que un 403 —la cuenta no puede agendar—
 * se tragaba en silencio y dejaba la lista en cero, exactamente igual que si
 * no hubiera nadie esperando. Quien lo veía no tenía forma de saber si le
 * faltaba permiso, si el servidor falló, o si de verdad no había candidatos.
 *
 * Vive aparte de la pantalla para poder probarlo sin montar el modal, y
 * porque lo usan las dos pantallas que listan estos candidatos.
 */

export interface EstadoDeLaCarga {
  /** Código HTTP, cuando la petición llegó a responder. */
  status?: number
  /** La petición no llegó a responder: red caída, CORS, servidor abajo. */
  fallo?: boolean
  /** Cuántos candidatos llegaron. */
  total: number
}

export interface AvisoDeCarga {
  /** `error` cuando hay algo que corregir; `aviso` cuando solo no hay nadie. */
  tono: 'error' | 'aviso'
  texto: string
}

export const SIN_PERMISO =
  'Tu cuenta no puede agendar la entrevista del solicitante. Pídele a RH que la agende, o que te asignen el acceso.'

export const NO_SE_PUDO_CARGAR =
  'No se pudo cargar la lista de candidatos. Vuelve a intentarlo en un momento.'

/**
 * Se nombra la condición que hay que cumplir, no solo el hueco: quien abre
 * esto suele venir de mover la tarjeta en el tablero, y lo que necesita saber
 * es que el candidato aparece al guardar los resultados de la entrevista.
 */
export const NADIE_LISTO =
  'No hay candidatos aprobados por RH esperando entrevista. Aparecen aquí en cuanto RH guarda los resultados de la entrevista con decisión «Aprobado por RH».'

/**
 * El aviso que toca, o null si hay candidatos y no hay nada que explicar.
 *
 * El orden importa: primero lo que la persona puede corregir —permiso, falla—
 * y al final el vacío legítimo. Un 403 con cero candidatos no es «no hay
 * candidatos»: es «no te dejaron verlos».
 */
export function avisoDeCandidatosParaAgendar(
  estado: EstadoDeLaCarga,
): AvisoDeCarga | null {
  if (estado.status === 401 || estado.status === 403) {
    return { tono: 'error', texto: SIN_PERMISO }
  }

  if (estado.fallo || (estado.status !== undefined && estado.status >= 400)) {
    return { tono: 'error', texto: NO_SE_PUDO_CARGAR }
  }

  if (estado.total === 0) {
    return { tono: 'aviso', texto: NADIE_LISTO }
  }

  return null
}

/**
 * El mismo criterio para un error ya convertido en excepción, que es como lo
 * recibe la pantalla del listado. El cliente HTTP deja el código en
 * `statusCode` o en `status` segun por donde haya pasado.
 */
export function avisoDeErrorAlCargar(error: unknown): AvisoDeCarga {
  const err = error as { statusCode?: number; status?: number }
  const status = err?.statusCode ?? err?.status

  return avisoDeCandidatosParaAgendar({ status, fallo: status === undefined, total: 0 }) ?? {
    tono: 'error',
    texto: NO_SE_PUDO_CARGAR,
  }
}
